import {gql} from '@apollo/client';
import {useMemo} from 'react';

import {SINGLETON_TASK_SCHEMA_NAME_TO_ENUM_KEY} from 'graphql-client/__generated/GeneratedEnumMaps';
import {ProcessSingletonTaskSchemaName} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {AllBoxSchemasWithRegistryQuery} from 'inventory/__generated/AllBoxSchemasWithRegistryQuery';
import {AllContainerSchemasWithRegistryQuery} from 'inventory/__generated/AllContainerSchemasWithRegistryQuery';
import {AllLocationSchemasWithRegistryQuery} from 'inventory/__generated/AllLocationSchemasWithRegistryQuery';
import {AllPlateSchemasWithRegistryQuery} from 'inventory/__generated/AllPlateSchemasWithRegistryQuery';
import {
  useBoxSchemasQueryWithRegistry,
  useContainerSchemasQueryWithRegistry,
  useLocationSchemasQueryWithRegistry,
  usePlateSchemasQueryWithRegistry,
} from 'inventory/useStorableSchemasQuery';
import {SchemaById} from 'lookup-config/LookupConfigModalConstants';
import {ProcessFlowchartEditorConfigurator} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {ALL_PROCESS_TASK_SCHEMAS_FOR_SCHEMA_CONFIG_QUERY} from 'processes/modals/utils/ProcessesAllSchemasQueryUtils';
import {ProcessStatusLifecyclesQuery} from 'processes/settings/__generated/ProcessStatusLifecyclesQuery';
import {STATUS_LIFECYCLES_QUERY} from 'processes/settings/ProcessTaskSchemaEditor';
import {insertOrReplaceTaskSchema} from 'processes/settings/ProcessTaskSchemaUtils';
import {AllResultSchemasQuery} from 'results/__generated/AllResultSchemasQuery';
import {useAllResultSchemasQuery} from 'results/useAllResultSchemasQuery';
import {TagSchemasWithRegistryQuery} from 'schemas/util/__generated/TagSchemasWithRegistryQuery';
import {useTagSchemasWithRegistryQuery} from 'schemas/util/useTagSchemasQuery';

import {ProcessTaskDefinitionGraphQL} from '../../../../../graphql-fragments/processes/__generated/ProcessTaskDefinitionGraphQL';
import {ProcessTaskDefinitionVersionForEditorGraphQL} from '../../../../../graphql-fragments/processes/__generated/ProcessTaskDefinitionVersionForEditorGraphQL';
import {ProcessTaskSchemaGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {AllConfigurableTaskDefinitionsQuery} from './__generated/AllConfigurableTaskDefinitionsQuery';
import {
  SingletonTaskSchemasByNameQuery,
  SingletonTaskSchemasByNameQueryVariables,
} from './__generated/SingletonTaskSchemasByNameQuery';
import {getSingletonTaskSchemaNamesForFlowchartConfig} from './processFlowchartConfigEditorUtils';

type TagSchemaGQL = TagSchemasWithRegistryQuery['tagSchemas'][number];
type ResultSchemasGQL = AllResultSchemasQuery['resultSchemas'][number];
type PlateSchemaGQL = AllPlateSchemasWithRegistryQuery['plateSchemas'][number];
type LocationSchemaGQL = AllLocationSchemasWithRegistryQuery['locationSchemas'][number];
type ContainerSchemaGQL = AllContainerSchemasWithRegistryQuery['containerSchemas'][number];
type BoxSchemaGQL = AllBoxSchemasWithRegistryQuery['boxSchemas'][number];
type StorableSchemaGQL = BoxSchemaGQL | ContainerSchemaGQL | LocationSchemaGQL | PlateSchemaGQL;
type ProcessTaskStatusLifecycleGQL = ProcessStatusLifecyclesQuery['processTaskStatusLifecycles'][number];
export type ConfigurableTaskDefinitionVersionGQL = ProcessTaskDefinitionVersionForEditorGraphQL & {
  processTaskSchema: ProcessTaskSchemaGraphQL;
};
export type ConfigurableTaskDefinitionGQL = ProcessTaskDefinitionGraphQL & {
  latestVersion: ConfigurableTaskDefinitionVersionGQL;
};

export type ProcessTaskDefinitionsResult = Array<ConfigurableTaskDefinitionGQL> | null;

export const ALL_CONFIGURABLE_TASK_DEFINITIONS_QUERY = gql`
  query AllConfigurableTaskDefinitionsQuery @engComponent(name: WORKFLOWS) @skipAudit {
    processTaskDefinitionsSupportingPlatformFeature(platformFeature: SCHEMA_CONFIG) {
      ...ProcessTaskDefinitionGraphQL
    }
  }
  ${getFragmentWrapper('ProcessTaskDefinition').fragment}
`;

export const SINGLETON_TASK_SCHEMAS_BY_NAMES_QUERY = gql`
  query SingletonTaskSchemasByNameQuery($names: [ProcessSingletonTaskSchemaName!]!, $orgId: StrictString!)
  @engComponent(name: WORKFLOWS)
  @skipAudit {
    processSingletonTaskSchemas(names: $names, orgId: $orgId) {
      ... on ProcessTaskSchemaNode {
        ...ProcessTaskSchemaGraphQL
      }
    }
  }
  ${getFragmentWrapper('ProcessTaskSchema').fragment}
`;

export function isConfigurableTaskDefinition(
  definition: ProcessTaskDefinitionGraphQL
): definition is ConfigurableTaskDefinitionGQL {
  const taskSchema = definition.latestVersion?.processTaskSchema;
  return taskSchema != null && !isInaccessible(taskSchema);
}

interface UseAllSchemasByIdResult {
  schemasById: SchemaById | null;
  processTaskDefinitionsResult: ProcessTaskDefinitionsResult;
  singletonTaskSchemasByName: Record<
    ProcessSingletonTaskSchemaName,
    ProcessTaskSchemaWithoutFlowchartConfigGraphQL
  > | null;
  processTaskSchemas: Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL> | null;
  statusLifecycles: Array<ProcessTaskStatusLifecycleGQL> | null;
  storableSchemas: Array<StorableSchemaGQL> | null;
  tagSchemas: Array<TagSchemaGQL> | null;
  resultSchemas: Array<ResultSchemasGQL> | null;
  haveSchemasLoaded: boolean;
}

interface UseAllSchemasByIdOptions {
  skipTaskDefinitionsQuery?: boolean;
  availableTaskSchemas?: Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL>;
}

export function useAllSchemasById(
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL | null,
  singletonTaskSchemaNames: Array<ProcessSingletonTaskSchemaName> = [],
  options: UseAllSchemasByIdOptions = {}
): UseAllSchemasByIdResult {
  const {skipTaskDefinitionsQuery = false, availableTaskSchemas} = options;

  const tagSchemasResult = useTagSchemasWithRegistryQuery();
  const boxSchemasResult = useBoxSchemasQueryWithRegistry();
  const containerSchemasResult = useContainerSchemasQueryWithRegistry();
  const locationSchemasResult = useLocationSchemasQueryWithRegistry();
  const plateSchemasResult = usePlateSchemasQueryWithRegistry();
  const resultSchemasResult = useAllResultSchemasQuery();
  const processTaskDefinitionsResult = useSkippableBenchlingQuery<AllConfigurableTaskDefinitionsQuery>(
    ALL_CONFIGURABLE_TASK_DEFINITIONS_QUERY,
    {skip: skipTaskDefinitionsQuery}
  );

  const singletonTaskSchemasResult = useSkippableBenchlingQuery<
    SingletonTaskSchemasByNameQuery,
    SingletonTaskSchemasByNameQueryVariables
  >(SINGLETON_TASK_SCHEMAS_BY_NAMES_QUERY, {
    variables: {
      names: singletonTaskSchemaNames,
      orgId: rootTaskSchema?.organization?.id ?? '',
    },
    skip: singletonTaskSchemaNames.length === 0 || rootTaskSchema == null,
  });

  const statusLifecyclesResult = useBenchlingQuery<ProcessStatusLifecyclesQuery>(STATUS_LIFECYCLES_QUERY);

  // Task definitions are resolved independently from core schema data so that the editor
  // can render immediately while the (heavy) definitions query is still in flight.
  const processTaskDefinitions = useMemo(() => {
    if (processTaskDefinitionsResult.type === QueryResultType.SUCCESS) {
      return processTaskDefinitionsResult.data.processTaskDefinitionsSupportingPlatformFeature.filter(
        (definition) => isConfigurableTaskDefinition(definition)
      );
    }
    if (processTaskDefinitionsResult.type === QueryResultType.SKIPPED) {
      return [];
    }
    // Return null (not []) on error so downstream null guards (e.g. in
    // FlowchartTransitionsSidepanelContent) disable interactive UI instead of
    // allowing modals to open with empty definitions and crash.
    if (processTaskDefinitionsResult.type === QueryResultType.ERROR) {
      return null;
    }
    return null; // pending
  }, [processTaskDefinitionsResult]);

  return useMemo(() => {
    const isSingletonQueryReady =
      singletonTaskSchemasResult.type === QueryResultType.SUCCESS ||
      singletonTaskSchemasResult.type === QueryResultType.SKIPPED;

    if (
      rootTaskSchema != null &&
      tagSchemasResult.type === QueryResultType.SUCCESS &&
      boxSchemasResult.type === QueryResultType.SUCCESS &&
      containerSchemasResult.type === QueryResultType.SUCCESS &&
      locationSchemasResult.type === QueryResultType.SUCCESS &&
      plateSchemasResult.type === QueryResultType.SUCCESS &&
      resultSchemasResult.type === QueryResultType.SUCCESS &&
      isSingletonQueryReady &&
      statusLifecyclesResult.type === QueryResultType.SUCCESS
    ) {
      let singletonTaskSchemasByName: Record<
        ProcessSingletonTaskSchemaName,
        ProcessTaskSchemaGraphQL
      > | null = null;
      if (singletonTaskSchemasResult.type === QueryResultType.SUCCESS) {
        const schemaNameToEnumKey = Object.fromEntries(
          Object.entries(SINGLETON_TASK_SCHEMA_NAME_TO_ENUM_KEY).map(([enumKey, schemaName]) => [
            schemaName,
            enumKey,
          ])
        );

        singletonTaskSchemasByName = Object.fromEntries(
          singletonTaskSchemasResult.data.processSingletonTaskSchemas
            .filter((schema): schema is ProcessTaskSchemaGraphQL => !isInaccessible(schema))
            .map((schema) => [schemaNameToEnumKey[schema.name], schema])
            .filter(([enumKey]) => enumKey !== undefined)
        ) as Record<ProcessSingletonTaskSchemaName, ProcessTaskSchemaGraphQL>;
      }

      const taskSchemasFromDefinitions = (processTaskDefinitions ?? []).map(
        (definition) => definition.latestVersion.processTaskSchema
      );
      const taskSchemasSource =
        taskSchemasFromDefinitions.length > 0 ? taskSchemasFromDefinitions : (availableTaskSchemas ?? []);

      const processTaskSchemas = insertOrReplaceTaskSchema(taskSchemasSource, rootTaskSchema);

      const storableSchemas = [
        ...boxSchemasResult.data.boxSchemas,
        ...containerSchemasResult.data.containerSchemas,
        ...locationSchemasResult.data.locationSchemas,
        ...plateSchemasResult.data.plateSchemas,
      ];

      const tagSchemas = tagSchemasResult.data.tagSchemas;
      const resultSchemas = resultSchemasResult.data.resultSchemas;

      const haveSchemasLoaded = [tagSchemas, processTaskSchemas, storableSchemas, resultSchemas].every(
        (arr) => arr != null
      );

      const schemasById = [
        ...(tagSchemas ?? []),
        ...(storableSchemas ?? []),
        ...(resultSchemas ?? []),
      ].reduce((acc, schema) => {
        acc[schema.id] = {...schema, fields: schema.fieldsJson, allFields: schema.allFieldsJson};
        return acc;
      }, {} as SchemaById);

      for (const taskSchema of processTaskSchemas ?? []) {
        schemasById[taskSchema.id] = taskSchema;
        if (taskSchema.outputSchema != null) {
          schemasById[taskSchema.outputSchema.id] = taskSchema.outputSchema;
        }
      }

      for (const singletonTaskSchema of Object.values(singletonTaskSchemasByName ?? []) ?? []) {
        schemasById[singletonTaskSchema.id] = singletonTaskSchema;
        if (singletonTaskSchema.outputSchema != null) {
          schemasById[singletonTaskSchema.outputSchema.id] = singletonTaskSchema.outputSchema;
        }
      }

      schemasById[rootTaskSchema.id] = rootTaskSchema;
      if (rootTaskSchema.outputSchema != null) {
        schemasById[rootTaskSchema.outputSchema.id] = rootTaskSchema.outputSchema;
      }

      const statusLifecycles = statusLifecyclesResult.data.processTaskStatusLifecycles;

      return {
        schemasById,
        storableSchemas,
        tagSchemas,
        resultSchemas,
        processTaskSchemas,
        processTaskDefinitionsResult: processTaskDefinitions,
        singletonTaskSchemasByName,
        statusLifecycles,
        haveSchemasLoaded,
      };
    }
    return {
      schemasById: null,
      storableSchemas: null,
      tagSchemas: null,
      resultSchemas: null,
      processTaskDefinitionsResult: skipTaskDefinitionsQuery ? [] : null,
      processTaskSchemas: null,
      singletonTaskSchemasByName: null,
      statusLifecycles: null,
      haveSchemasLoaded: false,
    };
  }, [
    rootTaskSchema,
    availableTaskSchemas,
    processTaskDefinitions,
    tagSchemasResult,
    boxSchemasResult,
    containerSchemasResult,
    locationSchemasResult,
    plateSchemasResult,
    resultSchemasResult,
    singletonTaskSchemasResult,
    statusLifecyclesResult,
    skipTaskDefinitionsQuery,
  ]);
}

export function useAllSchemasByIdWithSingletonSchemas(args: {
  flowchartEditorConfigurator: ProcessFlowchartEditorConfigurator | null;
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL | null;
  skipTaskDefinitionsQuery?: boolean;
  availableTaskSchemas?: Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL>;
}): UseAllSchemasByIdResult {
  // Pre-hydrate the AllProcessTaskSchemasForSchemaConfig Apollo cache so that
  // getAllProcessTaskSchemasFromCache() works before the lookup config modal is opened.
  // Hydrating here to avoid hook subscriptions to other callers of useAllSchemasById.
  useBenchlingQuery(ALL_PROCESS_TASK_SCHEMAS_FOR_SCHEMA_CONFIG_QUERY);

  const singletonTaskSchemas = useMemo(
    () =>
      args.flowchartEditorConfigurator != null && args.rootTaskSchema != null
        ? getSingletonTaskSchemaNamesForFlowchartConfig(args.flowchartEditorConfigurator, args.rootTaskSchema)
        : [],
    [args.flowchartEditorConfigurator, args.rootTaskSchema]
  );
  return useAllSchemasById(args.rootTaskSchema, singletonTaskSchemas, {
    skipTaskDefinitionsQuery: args.skipTaskDefinitionsQuery,
    availableTaskSchemas: args.availableTaskSchemas,
  });
}
